import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Card, Popconfirm, Segmented, Space, Switch, Table, Tag, Typography } from 'antd';
import { FormOutlined, PlusOutlined } from '@ant-design/icons';
import { NameCell, TableSearch } from '../../components/NameCell';
import type { FormItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { hostedFormUrl, useDeleteForm, useForms, useUpdateForm } from '../../features/forms/api';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { fmtDateTime } from '../../utils/format';
import { useProjectColumn } from '../../components/ProjectSelect';

export function FormsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const forms = useForms();
  const update = useUpdateForm();
  const remove = useDeleteForm();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<'all' | 'active' | 'inactive'>('all');
  const rows = useMemo(() => {
    const needle = q.toLowerCase();
    return (forms.data ?? []).filter((f) => (!needle || `${f.name} ${f.slug}`.toLowerCase().includes(needle)) && (active === 'all' || (active === 'active') === f.isActive));
  }, [forms.data, q, active]);
  const filtered = !!q || active !== 'all';
  const projectColumn = useProjectColumn<FormItem>();
  return (
    <div className="page">
      <PageHeader
        title="Forms"
        subtitle="Public forms that create or update contacts. Host them on their own page or embed them on your site."
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/forms/new')}>New form</Button>}
      />
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={q} onSearch={setQ} placeholder="Search forms" />
        </div>
        <div className="toolbar-group">
          <Segmented value={active} onChange={(v) => setActive(v as typeof active)} options={[{ value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        </div>
      </div>
      <Table<FormItem>
        rowKey="id"
        loading={tableLoading(forms)}
        locale={tableLocale(
          forms,
          filtered ? (
            <EmptyState description="No forms match this search or filter." action={<Button onClick={() => { setQ(''); setActive('all'); }}>Clear search and filter</Button>} />
          ) : (
            <EmptyState description="No forms yet. A form adds people to the CRM from your website." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/forms/new')}>Create your first form</Button>} />
          ),
        )}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 1000 }}
        columns={[
          {
            title: 'Form',
            dataIndex: 'name',
            width: 300,
            render: (v: string, f) => (
              <NameCell icon={<FormOutlined />} name={v} to={`/forms/${f.id}`} sub={<Typography.Text copyable={{ text: hostedFormUrl(f.slug), tooltips: ['Copy link', 'Copied'] }} type="secondary" style={{ fontSize: 13 }}>/f/{f.slug}</Typography.Text>} />
            ),
          },
          ...projectColumn,
          { title: 'Fields', key: 'fields', width: 80, render: (_, f) => f.fields.length },
          { title: 'Submissions', dataIndex: 'submissionCount', width: 120 },
          { title: 'Creates deal', key: 'deal', width: 120, render: (_, f) => (f.pipelineId ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>) },
          { title: 'Active', dataIndex: 'isActive', width: 90, render: (v: boolean, f) => <Switch size="small" aria-label={`${f.name} active`} checked={v} loading={update.isPending && update.variables?.id === f.id} onChange={(c) => update.mutateAsync({ id: f.id, isActive: c }).then(() => message.success(c ? 'Form activated' : 'Form deactivated')).catch((e) => message.error(errorMessage(e)))} /> },
          { title: 'Updated', dataIndex: 'updatedAt', width: 160, render: (v) => fmtDateTime(v) },
          {
            key: 'actions',
            align: 'right',
            render: (_, f) => (
              <Space>
                <Button size="small" onClick={() => window.open(hostedFormUrl(f.slug), '_blank')}>Open</Button>
                <Popconfirm title={`Delete form "${f.name}" and its submissions log?`} okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(f.id).then(() => message.success('Form deleted')).catch((e) => message.error(errorMessage(e)))}>
                  <Button size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
    </div>
  );
}
